import CheckFactionChip from '@/atoms/forms/CheckFactionChip';
import Spinner from '@/atoms/loader/Spinner';
import CustomMarkdown from '@/molecules/CustomMarkdown';
import { SIZE } from '@/types/utils';
import ModalClassicLayout from 'context/Modal/layout/Classic';
import { useFactions } from 'hooks/queries/factions';
import { usePostSeasonPassFaction } from 'hooks/queries/seasonPass';
import useUserApi from 'hooks/UserApi/useUserApi';
import Link from 'next/link';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CMSLinkType } from 'utils/cms/types';
import { formatFactionOptions } from 'utils/factions';
import BaseComponent from '../Filters/utils/BaseComponent';

interface Props {
  closeModal: () => void;
  title?: string;
  subtitle?: string;
  description?: string;
  link?: CMSLinkType;
}

type FormValues = {
  faction: string;
};

const SeasonPassFactionModal = ({ closeModal, title, subtitle, description, link }: Props) => {
  const { t } = useTranslation();
  const { user } = useUserApi();

  const { data: factions, isLoading } = useFactions();
  const factionsOptions = factions ? formatFactionOptions(factions) : {};

  const { mutate } = usePostSeasonPassFaction({
    onSettled: closeModal,
    userUniqueId: user?.uniqueId,
  });

  const formMethods = useForm<FormValues>();
  const handleSubmit = formMethods.handleSubmit((data) => {
    const factionApiRef = factionsOptions[data.faction]['@id'];
    mutate({ faction: factionApiRef! });
  });

  return (
    <ModalClassicLayout closeModal={closeModal} onSubmit={handleSubmit} title={title}>
      <div className="flex flex-col items-center gap-8 text-neutral-800">
        <h6 className="title-6">{subtitle}</h6>

        {description && <CustomMarkdown className="text-center xl:px-32">{description}</CustomMarkdown>}
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit}>
            <BaseComponent id="faction" title={t('factions')}>
              {isLoading ? (
                <Spinner size={SIZE.LG} />
              ) : (
                <CheckFactionChip isMulti={false} name="faction" options={factionsOptions} />
              )}
            </BaseComponent>
            <input className="hidden" type="submit" />
          </form>
        </FormProvider>

        {link && (
          <Link className="underline text-neutral-800" href={link.url} target={link.target}>
            {link.title}
          </Link>
        )}
      </div>
    </ModalClassicLayout>
  );
};

export default SeasonPassFactionModal;
